/**
 * FAQ accordion GA4 instrumentation.
 *
 * Publishes only `ocs:faq` through window.OCSAnalyticsEvents
 * (assets/ocs-analytics-events.js), the sole owner of the Shopify analytics
 * publish call — see the ARCH-001 guard in tests/analytics. The Custom Pixel
 * owns the dataLayer mapping: nothing here touches dataLayer, and nothing here
 * may, because GTM runs inside the Web Pixel sandbox and the storefront has no
 * dataLayer at all.
 *
 * This replaces the production GTM Custom HTML tag `HTML - FAQ listeners`
 * (tagId 167), which fired on a pageview trigger for the FAQ path and bound
 * click listeners to accordion headings. Sandboxed GTM cannot see the
 * storefront DOM, so that tag is dead here.
 *
 * There is most likely no historical `faq` volume to compare against, because
 * the GA4 tag appears never to have fired. Trigger `CE - FAQ` filters
 * {{Click Text}} on MATCH_REGEX `OPEN$` with no ignore_case flag, so the match
 * is case-sensitive, and the accordion's toggle label renders as `Open` / `Ouvrir`
 * (locales/en-CA.default.json, general.accessibility.open) under an explicit
 * `.accordion-item__title { text-transform: none }`. Neither string ends in a
 * capitalised OPEN. Worth confirming against GA4 rather than taking on trust —
 * if the `faq` event has no history, there is no continuity to protect and the
 * numbers this produces are the first real ones.
 *
 * Tag 167 had two further defects, which is why matching it exactly was never
 * the goal:
 *
 *   1. It cached `$("div.faq-page__blog-accordion").eq(0), .eq(2) … .eq(7)`,
 *      skipping `.eq(1)`. The FAQ page renders eight of those — Most Popular
 *      plus seven topic blogs — so the *first topic blog* never reported.
 *   2. It bound the listener to the whole accordion container, so any click
 *      inside it pushed `FAQ`, including collapses and clicks within an already
 *      open answer. The `OPEN$` filter existed to strain that noise back out.
 *
 * This module fixes both: it is bound per <details> and fires on open only, so
 * the trigger filter has no work left to do and can be removed.
 *
 * The payload uses the legacy hyphenated dataLayer keys `FAQ-section` and
 * `FAQ-title`, not this repo's usual snake_case. `FAQ-section` is what
 * {{DLV - faq section}} reads, and the pixel's key whitelist is a pass-through,
 * so matching it keeps the container change to a minimum.
 *
 * Both keys are sent on every event, empty where unresolved. GTM dataLayer
 * variables persist between pushes, so an omitted `FAQ-title` leaves the
 * previous question's title resolving on the next open.
 *
 * The current GTM-KBGGGQJ6 container triggers its FAQ tag from the lowercase
 * custom event `faq`. The Custom Pixel maps this module's `ocs:faq` publication
 * directly to that event while retaining the legacy FAQ-section and FAQ-title
 * dataLayer keys. No Click Text variable or storefront DOM access is required.
 */
(function () {
  'use strict';

  var EVENT_NAME = 'ocs:faq';

  /**
   * Unlocalized analytics key, deliberately: EN and FR search opens belong in
   * one GA4 bucket. Topic and featured sections stay localized, because that is
   * what tag 167 pushed (firstElementChild.innerText) and what
   * {{DLV - faq section}} has always received.
   */
  var SECTION_SEARCH = 'FAQ SEARCH';

  var ITEM_SELECTOR = '.faq-topics__item';
  var DETAILS_SELECTOR = '.details';
  var CATEGORY_SELECTOR = '.faq-topics__category';
  var CATEGORY_HEADING_SELECTOR = '.faq-topics__category-heading';
  var FEATURED_SELECTOR = '.faq-topics__featured';
  var FEATURED_HEADING_SELECTOR = '.faq-topics__featured-heading';

  /**
   * Set by #buildRow in assets/faq-search.js on every Algolia result row, and
   * on nothing else. #restore() puts the server markup back, so its presence is
   * an exact "this row came from the FAQ search" discriminator.
   */
  var RESULT_TITLE_SELECTOR = '.faq-topics__result-title';

  function publish(name, payload) {
    var api = window.OCSAnalyticsEvents;
    if (!api || typeof api.publishOcsAnalyticsEvent !== 'function') return false;
    return api.publishOcsAnalyticsEvent(name, payload);
  }

  function elementText(element) {
    var text = element && (element.innerText || element.textContent);
    return typeof text === 'string' ? text.trim() : '';
  }

  function closestOf(element, selector) {
    if (!element || typeof element.closest !== 'function') return null;
    return element.closest(selector);
  }

  function queryOf(element, selector) {
    if (!element || typeof element.querySelector !== 'function') return null;
    return element.querySelector(selector);
  }

  /* ------------------------------------------------------------------ *
   * DOM readers.
   * ------------------------------------------------------------------ */

  /**
   * The rendered heading of the group the row sits in.
   *
   * Search results are *not* rendered into a list of their own: #renderGrid in
   * assets/faq-search.js replaces each existing grid's contents in place, so a
   * result physically sits inside its origin .faq-topics__category. Resolving
   * by ancestry alone would report that topic's heading, which is why the
   * search branch is decided by the caller before this runs.
   *
   * @param {Element} details the opened <details>.
   * @returns {string} localized heading, or '' if neither container is found.
   */
  function readSection(details) {
    var category = closestOf(details, CATEGORY_SELECTOR);
    if (category) return elementText(queryOf(category, CATEGORY_HEADING_SELECTOR));

    var featured = closestOf(details, FEATURED_SELECTOR);
    if (featured) return elementText(queryOf(featured, FEATURED_HEADING_SELECTOR));

    return '';
  }

  /**
   * The question text.
   *
   * Read from data-faq-title on the row rather than from the <summary>, whose
   * text also covers the +/- icon span. snippets/faq-accordion-item.liquid
   * renders the attribute from article.title, and templates/blog.faq-ajax.liquid
   * renders that same snippet, so Load more rows carry it too.
   *
   * Search results are the exception and must never reach here: #buildRow in
   * assets/faq-search.js clones a *cached template row* — the first
   * .faq-topics__item on the page — so the clone's data-faq-title is that row's
   * title, not the result's.
   *
   * @param {Element} item the .faq-topics__item wrapper.
   * @returns {string}
   */
  function readTitle(item) {
    var title = item && item.dataset && item.dataset.faqTitle;
    return typeof title === 'string' ? title.trim() : '';
  }

  /* ------------------------------------------------------------------ *
   * Payload.
   * ------------------------------------------------------------------ */

  /**
   * @param {Element} details the opened <details>.
   * @param {Element} item its .faq-topics__item wrapper.
   * @returns {{'FAQ-section': string, 'FAQ-title': string}} both keys always
   *   present; '' where unresolved, never omitted.
   */
  function buildPayload(details, item) {
    var resultTitle = queryOf(details, RESULT_TITLE_SELECTOR);

    // One check drives both fields. A search result's section is the search,
    // not the topic grid it happens to be rendered into, and its title is the
    // rewritten span rather than the stale cloned attribute.
    if (resultTitle) {
      return {
        'FAQ-section': SECTION_SEARCH,
        'FAQ-title': elementText(resultTitle),
      };
    }

    return {
      'FAQ-section': readSection(details),
      'FAQ-title': readTitle(item),
    };
  }

  /* ------------------------------------------------------------------ *
   * Publish path.
   * ------------------------------------------------------------------ */

  /**
   * @param {Event} event a `toggle` event.
   * @returns {boolean} whether an event was published.
   */
  function handleToggle(event) {
    if (!event || !event.isTrusted) return false;

    var details = event.target instanceof HTMLDetailsElement ? event.target : null;

    // Open only. Production filtered on {{Click Text}} MATCH_REGEX `OPEN$`,
    // which is the filter that has to come off the trigger; enforcing the same
    // semantics here is what makes removing it safe.
    if (!details || details.open !== true) return false;

    var item = closestOf(details, ITEM_SELECTOR);
    if (!item) return false;

    // Answer bodies are merchant-authored RTE content and may contain their own
    // <details>. Only the row's own accordion counts — a nested one would
    // otherwise report the row's section and title as if the question had been
    // opened again.
    if (queryOf(item, DETAILS_SELECTOR) !== details) return false;

    return publish(EVENT_NAME, buildPayload(details, item));
  }

  function init(root) {
    if (!root || typeof root.addEventListener !== 'function') return;

    // Capture phase is load-bearing: `toggle` does not bubble, so a listener on
    // the document would never see it otherwise. Same pattern as the PDP
    // accordions at assets/ocs-pdp-analytics.js:300.
    //
    // Delegating on the document also means Load more and search-rebuilt rows
    // need no re-binding — faq-load-more.js appends into the existing list and
    // faq-search.js replaces it wholesale, both of which discard any listener
    // bound to a row.
    root.addEventListener('toggle', function onFaqToggle(event) {
      try {
        handleToggle(event);
      } catch (error) {
        /* Analytics must never block opening an answer. */
      }
    }, true);
  }

  window.OCSFaqAnalytics = {
    buildPayload: buildPayload,
    handleToggle: handleToggle,
    init: init,
    readSection: readSection,
    readTitle: readTitle,
    SECTION_SEARCH: SECTION_SEARCH,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function bootstrap() {
      init(document);
    });
  } else {
    init(document);
  }
})();
